import { Agent, ElasticSearchAgent, PlatformAgentExportMeta } from '@ag-common-lib/public-api';
import { LoadOptions } from 'devextreme/data';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';

/**
 * Rows plus the flat-file layout parameters: the most emails/addresses/phones any
 * matched agent carries, i.e. how many banded columns the export needs. The backend
 * computes these over the filtered set; the browser builds the file from them.
 */
export interface AgentExportData {
  data: ElasticSearchAgent[];
  meta: PlatformAgentExportMeta;
}

export interface AgentSearchResult {
  /**
   * The projected agent, not the whole Firestore document. Both backends answer with the
   * fields the index carried, which is what every consumer of this call reads.
   */
  data: ElasticSearchAgent[];
  totalCount?: number;
  groupCount?: number;
  summary?: unknown[];
}

export interface AgentEmailAddressGroup {
  /** The address itself. Named for the field the grouped view binds as its key. */
  email_addresses: string;
  agentsCount: number;
  agents: ElasticSearchAgent[];
}

export interface AgentEmailAddressGroupsResult {
  data: AgentEmailAddressGroup[];
  totalCount: number;
}

export interface AgentSearchStoreOptions extends LoadOptions {
  postProcess?: (response: AgentSearchResult) => unknown;
}

/**
 * How the apps ask about agents, independent of what answers.
 *
 * Declared as an abstract class rather than an interface so it can be a DI token, bound
 * to `AgentSqlSearchService`. It stays a seam rather than collapsing into the concrete
 * class: consumers depend on the question, not on where the answer comes from, and that
 * is what let the backend be replaced under thirty of them without touching one.
 *
 * The surface is the one the Elasticsearch service exposed, minus the part that leaked
 * the index into its caller:
 *
 * - The two-step email aggregation (`getAggregations` + `normalizeAggregations`) made its
 *   one caller assemble buckets and then fetch their agents. `getEmailAddressGroups`
 *   answers the question instead, which each backend can do in whatever way suits it.
 */
export abstract class AgentSearchService {
  /** DevExtreme load options in, rows out. */
  abstract search(loadOptions: LoadOptions): Promise<AgentSearchResult>;

  /** By Firestore document id. Null for an empty id or a miss. */
  abstract getById(dbId: string): Promise<Agent>;

  abstract getByIds(dbIds: string[]): Promise<Agent[]>;

  /**
   * The signed-in agent's own record.
   *
   * Separate from `getById` because it takes no id: the server reads it from the verified
   * token, so this is the one call an agent can make before they are approved. The login
   * flow needs it — an agent under review must be able to load themselves to be shown why
   * they are blocked — and it is what replaced the last direct Firestore agent read.
   */
  abstract getSelf(): Promise<Agent>;

  /**
   * The agents holding an address — the primary email or any of the agent's own email
   * addresses.
   *
   * Wider than the `p_email ==` query this replaces, deliberately: every caller is
   * asking whose address this is, and an agent carrying it as a secondary is the same
   * person. Firestore missed them because an agent query cannot reach into the
   * email-addresses sub-collection, not because the answer was wanted narrower. Callers
   * needing the distinction have it — each agent carries its `email_addresses`.
   */
  abstract getByEmail(email: string): Promise<Agent[]>;

  /**
   * The batch form, for the callers resolving a fixed list. Keyed by the address as it
   * was passed, so a caller looks its own strings back up rather than guessing at how
   * they were normalized. An address matching nobody is present with an empty array.
   */
  abstract getByEmails(emails: string[]): Promise<Map<string, Agent[]>>;

  /** By business id (`p_agent_id`). The first match, or undefined. */
  abstract getByAgentId(agentId: string): Promise<Agent>;

  abstract getAgentsByAgentId(agentId: string): Promise<Agent[]>;

  /** Every agent below this one, at any depth, excluding the agent itself. */
  abstract getTeamByAgentId(agentId: string): Promise<Agent[]>;

  abstract getDataSource(options?: AgentSearchStoreOptions): DataSource;

  abstract getStore(options?: AgentSearchStoreOptions): CustomStore;

  /** Distinct email addresses, each with the agents holding it. */
  abstract getEmailAddressGroups(loadOptions: LoadOptions): Promise<AgentEmailAddressGroupsResult>;

  /**
   * Agents that have a mappable address, nearest first when a point is given. Returns the
   * rows themselves rather than a wrapper, which is the shape the map's store expects.
   */
  abstract getAgentsGeoPoints(loadOptions: LoadOptions): Promise<ElasticSearchAgent[]>;

  /**
   * Every agent matching the filter (and any explicit search text), with the layout
   * parameters a flat export needs. Unpaged by definition — the file wants the whole
   * matched set — and bounded server-side on the SQL backend.
   */
  abstract getExportData(loadOptions: LoadOptions): Promise<AgentExportData>;
}
